import { environment } from './../../../../environments/environment';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { firstValueFrom, merge, Subject, Subscription } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';
import { FormRequest } from './form-request';
import type { Form, FormField } from './interfaces/form.interface';
import { FileUploadAnswer } from '../../utils';
import { DropdownData, InputType } from './interfaces/input.interface';
import { RequestHandler } from '../../../../../src/app/service/OffService/request-handler';
import { FormOperations } from './utils/form-operations';
import { FormCustomValidators } from './utils/form-custom-validators';

@Component({
  selector: 'app-reactive-form-builder',
  standalone: false,
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReactiveFormBuilderComponent<T>
  extends FormRequest
  implements OnInit, OnDestroy, OnChanges {

  @Input() form!: Form<T>;
  @Input() id = '';
  @Input() previousData: T | null = null;
  @Input() serverUrl = '';

  // IMPORTANT: keep as any to avoid template variance errors (RequestHandler<T[]> -> RequestHandler<unknown> is NOT assignable)
  @Input() request!: RequestHandler<any>;
  @Input() getRequest!: RequestHandler<any>;

  @Input() undoEnabled = false;
  @Input() recordHasAssociatedData = false;

  /**
   * Progressive dropdown loading trigger.
   * Parent should preferably pass ReplaySubject(1).
   */
  @Input() dropdownLoadTrigger: Subject<void> | null = null;

  /**
   * True when parent finished loading ALL dropdown lists.
   * Used to decide when "unknown ids" must be cleared.
   */
  @Input() dropdownsFinalized = false;

  @Input() showActionsButtons = true;

  @Output() formChanges: EventEmitter<[[object, string], string]> =
    new EventEmitter<[[object, string], string]>();

  @Output() goBack: EventEmitter<T> = new EventEmitter<T>();
  @Output() afterSend: EventEmitter<T> = new EventEmitter<T>();
  @Output() hideDialog: EventEmitter<T> = new EventEmitter<T>();

  public appName = environment.appName;
  public filterInputParcelas = environment.features.filterInputParcelas;
  public INPUT = InputType;

  public model!: FormGroup;
  public originalModel: Record<string, string> = {};
  public isLoading = false;

  private static readonly DROPDOWN_TRIGGER_AUDIT_MS = 50;

  private readonly destroy$ = new Subject<void>();

  private previousModel: Record<string, unknown> = {};
  private formOperations!: FormOperations<T>;
  private formCustomValidators = new FormCustomValidators();

  private twoFieldsConcatSubs = new Subscription();
  private dropdownTriggerSub: Subscription | null = null;

  private previousDataReady = false;
  private needsDropdowns = false;

  /**
   * NOTE:
   * - `cdr` is optional on purpose because you are instantiating this class with `new ReactiveFormBuilderComponent(this.http)`
   *   in some screens. Angular DI will provide it when used as a real component.
   */
  constructor(
    private http: HttpClient,
    private cdr?: ChangeDetectorRef,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.needsDropdowns = (this.form?.type <= 2 || this.form?.type === 6) && !!this.dropdownLoadTrigger;

    // Render fast: build form immediately and patch progressively.
    this.initializeModel();
    this.indexRepeatableBindingControls()
    this.subscribeToFormChanges();

    this.originalModel = { ...(this.model.getRawValue() as Record<string, string>) };
    this.formOperations.executeFieldsLogic(null);

    // Subscribe ASAP (ReplaySubject in parent avoids missing early emits).
    this.registerDropdownTrigger(this.dropdownLoadTrigger);

    // If previousData already present from parent (create/duplicate), patch now.
    if (!this.id && this.previousData != null) {
      this.previousDataReady = true;

      this.patchFromPreviousDataPristine();
      this.patchRepeatableBindingsFromPreviousDataPristine(); // <- AÑADIR
      this.remapDropdownFieldsPristine(); // remap with current options (may be empty yet)

      this.formOperations.executeFieldsLogic(null);
      this.originalModel = { ...(this.model.getRawValue() as Record<string, string>) };

      this.updateLoadingState();
      this.cdr?.markForCheck();
    }

    // Edit mode: fetch previousData if id provided.
    if (this.id) {
      this.previousDataReady = false;
      this.updateLoadingState();

      this.getRequest.safePerform({ id: this.id });
      this.getRequest.response((res: unknown) => {
        if (!this.getRequest.hasError) {
          const arr = res as unknown as Array<unknown> | undefined;
          const first = Array.isArray(arr) ? arr[0] : null;

          if (first && (first as any).id?.toString() !== this.id?.toString()) {
            return;
          }

          this.previousData = (first as T) ?? null;
          this.previousDataReady = true;

          this.patchFromPreviousDataPristine();
          this.patchRepeatableBindingsFromPreviousDataPristine();
          this.remapDropdownFieldsPristine();

          this.formOperations.executeFieldsLogic(null);
          this.originalModel = { ...(this.model.getRawValue() as Record<string, string>) };

          this.updateLoadingState();
          this.cdr?.markForCheck();
        }
      });

      return;
    }

    this.previousDataReady = true;
    this.updateLoadingState();
    this.cdr?.markForCheck();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('dropdownLoadTrigger' in changes) {
      const trigger = changes['dropdownLoadTrigger']?.currentValue as Subject<void> | null;

      this.needsDropdowns = (this.form?.type <= 2 || this.form?.type === 6) && !!trigger;
      this.registerDropdownTrigger(trigger);

      this.updateLoadingState();
      this.cdr?.markForCheck();
    }

    if ('dropdownsFinalized' in changes) {
      // 🔥 IMPORTANT: when finalized toggles to true, do a final remap+cleanup
      const nowFinal = Boolean(changes['dropdownsFinalized']?.currentValue);

      if (nowFinal) {
        this.remapDropdownFieldsPristine();

        this.formOperations.executeFieldsLogic(null);
        this.originalModel = { ...(this.model.getRawValue() as Record<string, string>) };
      }

      this.updateLoadingState();
      this.cdr?.markForCheck();
    }

    if ('previousData' in changes) {
      const current = changes['previousData']?.currentValue as T | null | undefined;

      if (current != null) {
        this.previousDataReady = true;

        this.patchFromPreviousDataPristine();
        this.patchRepeatableBindingsFromPreviousDataPristine();
        this.remapDropdownFieldsPristine();

        this.formOperations.executeFieldsLogic(null);
        this.originalModel = { ...(this.model.getRawValue() as Record<string, string>) };

        this.updateLoadingState();
        this.cdr?.markForCheck();
      }
    }
  }

  public ngOnDestroy(): void {
    this.twoFieldsConcatSubs.unsubscribe();
    this.dropdownTriggerSub?.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
  }

  public returnToMainView(): void {
    this.goBack.emit();
  }

  public executePostSubmissionLogic(res: T): void {
    this.afterSend.emit(res);
  }

  public closeDialog(): void {
    this.hideDialog.emit();
  }

  public async getSignature(event: File): Promise<void> {
    this.model.patchValue({ ['signature_img']: event });

    const signatureFile = this.model.get('signature_img')?.value as File | null;
    if (signatureFile == null) {
      return;
    }

    const formData = new FormData();

    formData.append('fileToUpload', signatureFile);
    formData.append('database', 'tareas_fitosanitarias');
    formData.append('folder', 'fitosanitarios');
    formData.append('field', 'signature_img');

    const signature = await firstValueFrom(
      this.http.post<FileUploadAnswer>(
        this.serverUrl + 'ws/tareas/fileUploadGeneric.php',
        formData,
      ),
    );

    this.model.patchValue({
      ['signature_img']: signature.target_file.replace('../../images/fitosanitarios/', ''),
    });
  }

  public updateFirma(event: string): void {
    this.model.patchValue({ ['signature_img']: event });
  }

  public updateImageModel(event: string, fieldName: string): void {
    this.model.patchValue({ [fieldName]: event });
  }

  public updateForm(data: Record<string, unknown>, emit: boolean): void {
    this.model.patchValue(data, { emitEvent: emit });
  }

  /**
   * Progressive field rendering: ready when list has real options (or all dropdowns finalized).
   * IMPORTANT: if list is empty and finalized, field must still render (empty dropdown, not removed).
   */
  public isFieldReady(field: FormField<T>): boolean {
    const dd = field?.input?.data as DropdownData<unknown> | undefined;

    const isDropdownLike =
      !!field.input?.dropdownConfiguration?.valuePrimaryKey ||
      Array.isArray(dd?.values) ||
      Array.isArray(dd?.filtered);

    if (!isDropdownLike) {
      return true;
    }

    const list = (dd?.filtered ?? dd?.values ?? []) as Array<{ label?: string; value: unknown }>;

    const hasRealOptions = list.some(it => {
      const label = String(it?.label ?? '').trim();
      return label.length > 0 && label !== '...' && it?.value != null;
    });

    return hasRealOptions || this.dropdownsFinalized;
  }

  /**
   * Small preview while the field is not ready.
   */
  public getPendingDisplay(field: FormField<T>): string {
    const name = field.name ?? '';
    if (!name) {
      return '';
    }

    const control = this.model?.get(name) as FormControl | null;
    if (!control) {
      return '';
    }

    return this.formatPreviewValue(control.value, field);
  }

  private getDropdownOptions(
  dd?: DropdownData<unknown>,
): Array<{ label?: string; value: unknown }> {
  const filtered = dd?.filtered as Array<{ label?: string; value: unknown }> | undefined;
  if (Array.isArray(filtered) && filtered.length > 0) return filtered;

  const values = dd?.values as Array<{ label?: string; value: unknown }> | undefined;
  return Array.isArray(values) ? values : [];
}


  private formatPreviewValue(value: unknown, field: FormField<T>): string {
    const normalize = (s: string): string => s.trim();

    const isBadToken = (s: string): boolean => {
      const t = normalize(s).toLowerCase();
      return t === 'null' || t === 'undefined' || t === 'invalid date';
    };

    const pk = field.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';

    const toText = (v: unknown): string => {
      if (v == null) {
        return '';
      }

      if (typeof v === 'string') {
        return isBadToken(v) ? '' : v;
      }

      if (typeof v === 'number' || typeof v === 'boolean') {
        return String(v);
      }

      if (typeof v === 'object') {
        const r = v as Record<string, unknown>;

        const candidates: Array<unknown> = [
          r['label'],
          r['nombre'],
          r['name'],
          r['razon_social'],
          r['descripcion'],
          r[pk],
          r['id'],
        ];

        for (const c of candidates) {
          if (typeof c === 'string' && !isBadToken(c) && c.trim().length > 0) {
            return c.trim();
          }
          if (typeof c === 'number') {
            return String(c);
          }
        }

        return '';
      }

      return '';
    };

    if (Array.isArray(value)) {
      const parts = value.map(toText).filter(s => s.length > 0);
      if (parts.length === 0) {
        return '';
      }

      const shown = parts.slice(0, 3);
      const rest = parts.length - shown.length;

      return rest > 0 ? `${shown.join(', ')} +${rest}` : shown.join(', ');
    }

    return toText(value);
  }

  private initializeModel(): void {
    this.model = this.toFormGroup(this.form);

    this.setupTwoFieldsConcat();

    // Create FormOperations AFTER model exists
    this.formOperations = new FormOperations(this.form, this.model);

    // Sync selected values after model exists
    this.applyInitialSelectedValues();
  }

  private subscribeToFormChanges(): void {
    this.model.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((newValuesRaw: unknown) => {
        const newValues = newValuesRaw as Record<string, string>;

        const changedFields = Object.keys(newValues).filter(
          (control) => newValues[control] !== this.previousModel?.[control],
        );

        if (changedFields.length > 0) {
          let hasRepeatableChange = false;

          for (const changedField of changedFields) {
            if (this.repeatableBindingControlNames.has(changedField)) {
              hasRepeatableChange = true;
              continue;
            }
            this.formChanges.emit([newValues as any, changedField]);

            this.formOperations.applySelected(
              this.formOperations.getFieldByControlName(changedField),
              newValues[changedField] as T,
            );

            this.formOperations.executeFieldsLogic(changedField);
          }

          if (hasRepeatableChange) {
            this.formChanges.emit([newValues as any, '__repeatable__']);
            this.formOperations.executeFieldsLogic(null);
          }
        }

        this.previousModel = { ...newValues };
      });
}

  /**
   * Dropdown trigger can be emitted many times (progressive loading).
   * We remap and debounce to avoid heavy work.
   */
  private registerDropdownTrigger(trigger: Subject<void> | null): void {
    this.dropdownTriggerSub?.unsubscribe();
    this.dropdownTriggerSub = null;

    if (!trigger) {
      return;
    }

    this.dropdownTriggerSub = trigger
      .pipe(
        auditTime(ReactiveFormBuilderComponent.DROPDOWN_TRIGGER_AUDIT_MS),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.remapDropdownFieldsPristine();

        this.formOperations.executeFieldsLogic(null);
        this.originalModel = { ...(this.model.getRawValue() as Record<string, string>) };

        this.updateLoadingState();
        this.cdr?.markForCheck();
      });
  }

  private updateLoadingState(): void {
    const waitingPrevious = !!this.id && !this.previousDataReady;
    const waitingDropdowns = this.needsDropdowns && !this.dropdownsFinalized;

    this.isLoading = waitingPrevious || waitingDropdowns;
  }

  /**
   * Patch previousData into pristine controls only.
   */
  private patchFromPreviousDataPristine(): void {
    for (const section of this.form.sections) {
      if (!section.isVisible) { continue; }

      for (const field of section.fields) {
        if (!field.conditions.isVisible || field.conditions.isRetained) { continue; }

        const name = field.name ?? '';
        if (!name) { continue; }

        if (field.input?.type === (InputType as any).REPEATABLE_GROUP) { continue; }

        const control = this.model.get(name) as FormControl | null;
        if (!control) { continue; }

        // Do not override user edits
        if (!control.pristine) { continue; }

        const nextValue = this.setDefaultValues(field);

        control.patchValue(nextValue, { emitEvent: false });
      }
    }

    this.applyInitialSelectedValues();
  }

  /**
   * Remap dropdowns (ids -> option objects) on pristine controls.
   * IMPORTANT:
   * - If dropdownsFinalized === true and a value doesn't exist in options,
   *   we clear it (avoid PrimeNG showing "null" / "null, null").
   */
  private remapDropdownFieldsPristine(): void {
    for (const section of this.form.sections) {
      if (!section.isVisible) { continue; }

      for (const field of section.fields) {
        if (!field.conditions.isVisible || field.conditions.isRetained) { continue; }

        const name = field.name ?? '';
        if (!name) { continue; }

        if (field.input?.type === (InputType as any).REPEATABLE_GROUP) { continue; }
        if (field.input?.type === InputType.TWO_FIELDS_CONCAT) { continue; }

        const control = this.model.get(name) as FormControl | null;
        if (!control) { continue; }

        // Do not override user edits
        if (!control.pristine) { continue; }

        const pk = field.input?.dropdownConfiguration?.valuePrimaryKey;
        if (!pk) { continue; }

        const isMulti =
          field.input?.type === InputType.MULTISELECT ||
          field.input?.type === InputType.MULTISELECT_SEARCH;

          const dd = field?.input?.data as DropdownData<unknown> | undefined;
          const options = this.getDropdownOptions(dd);


        // If no options and finalized => force clear (avoid "null" ghost selections)
        if (options.length === 0) {
          if (this.dropdownsFinalized) {
            const cleared = isMulti ? [] : '';
            if (!this.areValuesEquivalent(control.value, cleared)) {
              control.patchValue(cleared, { emitEvent: false });

              this.formOperations.applySelected(
                this.formOperations.getFieldByControlName(name),
                cleared as T,
              );
            }
          }
          continue;
        }

        const currentValue = control.value;
        const nextValue = this.remapValueWithOptions(currentValue, options, pk, isMulti);

        if (this.areValuesEquivalent(currentValue, nextValue)) { continue; }

        control.patchValue(nextValue, { emitEvent: false });

        this.formOperations.applySelected(
          this.formOperations.getFieldByControlName(name),
          nextValue as T,
        );
      }
    }
  }

  private remapValueWithOptions(
    value: unknown,
    options: Array<{ label?: string; value: unknown }>,
    pk: string,
    isMulti: boolean,
  ): unknown {
    const normalizeToken = (v: string): string => v.trim();

    const isBadToken = (v: string): boolean => {
      const t = normalizeToken(v).toLowerCase();
      return t === 'null' || t === 'undefined' || t === 'invalid date';
    };

    const mapIdToValue = (id: string | number): unknown | null => {
      const found = options.find((o) => {
        const raw = (o && typeof o === 'object') ? (o as any).value : o;

        if (raw == null) {
          return false;
        }

        if (typeof raw !== 'object') {
          return String(raw) === String(id);
        }

        return String((raw as Record<string, unknown>)[pk]) === String(id);
      });

      return found ? (found as any).value : null;
    };

    // Multi
    if (isMulti) {
      const arr: unknown[] = Array.isArray(value) ? value : [];

      const looksLikeObjects = arr.length > 0 && arr.every((x) => {
        if (!x || typeof x !== 'object') { return false; }
        return (x as Record<string, unknown>)[pk] != null;
      });

      if (looksLikeObjects) {
        return arr;
      }

      const ids = arr
        .map((v) => {
          if (typeof v === 'number') { return v; }
          if (typeof v === 'string') { return isBadToken(v) ? null : v; }

          if (typeof v === 'object' && v !== null) {
            const rec = v as Record<string, unknown>;
            const fromPk = rec[pk];
            const fromId = rec['id'];

            if (typeof fromPk === 'number') { return fromPk; }
            if (typeof fromPk === 'string' && !isBadToken(fromPk)) { return fromPk; }

            if (typeof fromId === 'number') { return fromId; }
            if (typeof fromId === 'string' && !isBadToken(fromId)) { return fromId; }
          }

          return null;
        })
        .filter((v): v is string | number => v != null);

      const mapped = ids.map((id) => mapIdToValue(id)).filter((v) => v != null);

      if (mapped.length > 0) {
        return mapped;
      }

      // ✅ KEY FIX: if finalized and nothing maps => clear (avoid "null, null")
      if (this.dropdownsFinalized) {
        return [];
      }

      // Keep ids temporarily until next trigger
      return ids.length > 0 ? ids : arr;
    }

    // Single
    if (value == null) {
      return '';
    }

    if (typeof value === 'object' && (value as Record<string, unknown>)[pk] != null) {
      return value;
    }

    if (typeof value === 'string' && isBadToken(value)) {
      return '';
    }

    const mapped = mapIdToValue(value as string | number);

    if (mapped != null) {
      return mapped;
    }

    // ✅ KEY FIX: if finalized and no match => clear (avoid "null")
    if (this.dropdownsFinalized) {
      return '';
    }

    // Keep raw id temporarily until next trigger
    return value;
  }

  private areValuesEquivalent(a: unknown, b: unknown): boolean {
    if (a === b) {
      return true;
    }

    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) {
        return false;
      }

      for (let i = 0; i < a.length; i += 1) {
        if (String(a[i]) !== String(b[i])) {
          return false;
        }
      }

      return true;
    }

    return String(a ?? '') === String(b ?? '');
  }

  private applyInitialSelectedValues(): void {
    for (const section of this.form.sections) {
      if (!section.isVisible) { continue; }

      for (const field of section.fields) {
        if (!field.conditions.isVisible || field.conditions.isRetained) { continue; }

        const name = field.name ?? '';
        if (!name) { continue; }

        if (field.input?.type === (InputType as any).REPEATABLE_GROUP) { continue; }

        const control = this.model.get(name) as FormControl | null;
        if (!control) { continue; }

        this.formOperations.applySelected(
          this.formOperations.getFieldByControlName(name),
          control.value as T,
        );
      }
    }
  }

  /**
   * Converts form fields to FormGroup.
   * IMPORTANT: Keep this method "pure" (do not call formOperations here).
   */
  private toFormGroup(form: Form<T>): FormGroup {
    const controls = form.sections
      .filter((section) => section.isVisible)
      .flatMap((section) => section.fields)
      .filter((field) => field.conditions.isVisible && !field.conditions.isRetained)
      .reduce((group, field) => {

        // Repeatable group
        if (field.input?.type === (InputType as any).REPEATABLE_GROUP) {
          group[field.name ?? ''] = new FormControl([], this.setValidators(field));

          const cfg = field.input?.repeatableGroupConfiguration;
          const bindings = cfg?.bindings ?? {};
          const prev = (this.previousData as Record<string, unknown>) ?? {};

          for (const [, targetControlName] of Object.entries(bindings)) {
            const shouldForget =
              this.form.type === 2 && field.conditions.isRememberedOnDuplicate === false;

            const prevRaw = shouldForget ? '' : (prev?.[targetControlName] ?? '');

            if (!group[targetControlName]) {
              group[targetControlName] = new FormControl(
                String(prevRaw ?? ''),
                [],
              );
            }
          }

          return group;
        }

        const defaultValue = this.setDefaultValues(field);
        const validators = this.setValidators(field);

        // Two fields concat
        if (field.input?.type === InputType.TWO_FIELDS_CONCAT) {
          const cfg = field.input.twoFieldsConcatConfiguration;

          const sub1Name = cfg?.subField1 ?? '';
          const sub2Name = cfg?.subField2 ?? '';
          const sep = cfg?.separator ?? 'x';

          const parentDefaultRaw = this.setDefaultValues(field);
          const parentDefault = String(parentDefaultRaw ?? '').trim();

          const [sub1Default, sub2Default] = this.splitTwoFieldsConcat(parentDefault, sep);

          group[field.name ?? ''] = new FormControl(parentDefault, validators);

          if (sub1Name) { group[sub1Name] = new FormControl(sub1Default, validators); }
          if (sub2Name) { group[sub2Name] = new FormControl(sub2Default, validators); }

          return group;
        }

        group[field.name ?? ''] = new FormControl(defaultValue, validators);

        return group;
      }, {} as Record<string, FormControl>);

    return new FormGroup(controls);
  }

  private setupTwoFieldsConcat(): void {
    this.twoFieldsConcatSubs.unsubscribe();
    this.twoFieldsConcatSubs = new Subscription();

    for (const section of this.form.sections) {
      if (!section.isVisible) { continue; }

      for (const field of section.fields) {
        if (!field.conditions.isVisible || field.conditions.isRetained) { continue; }
        if (field.input?.type !== InputType.TWO_FIELDS_CONCAT) { continue; }

        const cfg = field.input.twoFieldsConcatConfiguration;
        const parentName = field.name ?? '';
        const sub1 = cfg?.subField1 ?? '';
        const sub2 = cfg?.subField2 ?? '';
        const sep = cfg?.separator ?? 'x';

        if (!parentName || !sub1 || !sub2) { continue; }

        const cParent = this.model.get(parentName) as FormControl | null;
        const c1 = this.model.get(sub1) as FormControl | null;
        const c2 = this.model.get(sub2) as FormControl | null;

        if (!cParent || !c1 || !c2) { continue; }

        const initial = String(cParent.value ?? '');
        if (
          initial.length > 0 &&
          (String(c1.value ?? '').length === 0) &&
          (String(c2.value ?? '').length === 0)
        ) {
          const parts = initial.split(sep);
          c1.patchValue((parts[0] ?? '').trim(), { emitEvent: false });
          c2.patchValue((parts[1] ?? '').trim(), { emitEvent: false });
        }

        this.twoFieldsConcatSubs.add(
          merge(c1.valueChanges, c2.valueChanges).subscribe(() => {
            const v1 = String(c1.value ?? '').trim();
            const v2 = String(c2.value ?? '').trim();

            const combined = (v1.length === 0 && v2.length === 0) ? '' : `${v1}${sep}${v2}`;

            if (String(cParent.value ?? '') !== combined) {
              cParent.patchValue(combined, { emitEvent: true });
            }
          }),
        );
      }
    }
  }

  private splitTwoFieldsConcat(value: string, separator: string): [string, string] {
    if (!value) { return ['', '']; }

    const sep = (separator ?? '').trim();
    if (!sep) { return [value, '']; }

    const idx = value.indexOf(sep);
    if (idx < 0) { return [value, '']; }

    const left = value.slice(0, idx).trim();
    const right = value.slice(idx + sep.length).trim();

    return [left, right];
  }

  /**
   * Safe default values:
   * - Multiselect always returns array.
   * - Single returns '' unless previous/default exists.
   *
   * IMPORTANT:
   * - When dropdownsFinalized === true, unknown ids are cleared (avoid "null").
   */
  private setDefaultValues(field: FormField<T>): unknown {
    const isRepeatable = field?.input?.type === (InputType as any).REPEATABLE_GROUP;
    if (isRepeatable) {
      return [];
    }

    const isMulti =
      field?.input?.type === InputType.MULTISELECT ||
      field?.input?.type === InputType.MULTISELECT_SEARCH;

    if (this.form.type === 2 && field.conditions.isRememberedOnDuplicate === false) {
      return isMulti ? [] : '';
    }

    const key = field.name ?? '';
    const aliasKey = key.endsWith('_nombres') ? `ids_${key.replace(/_nombres$/, '')}` : undefined;

    const previousData = this.previousData as Record<string, unknown> | undefined;
    const prevRaw =
      previousData?.[key] ??
      (aliasKey ? previousData?.[aliasKey] : undefined);

    const normalizeToken = (value: string): string => value.trim();

    const isBadToken = (value: string): boolean => {
      const token = normalizeToken(value).toLowerCase();
      return token === 'null' || token === 'undefined' || token === 'invalid date';
    };

    const splitIds = (value: string): string[] =>
      value
        .split(/[;,]/)
        .map(normalizeToken)
        .filter(v => v.length > 0 && !isBadToken(v));

    const getOptions = (): Array<{ label?: string; value: unknown }> => {
      const dd = field?.input?.data as DropdownData<unknown> | undefined;
      const options = this.getDropdownOptions(dd);
      return options;
    };

    const options = getOptions();

    const mapIdToValue = (id: string | number): unknown | null => {
      const pk = field.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';

      const found = options.find(o => {
        const raw = (o && typeof o === 'object') ? (o as any).value : o;

        if (raw == null) {
          return false;
        }

        if (typeof raw !== 'object') {
          return String(raw) === String(id);
        }

        return String((raw as Record<string, unknown>)[pk]) === String(id);
      });

      return found ? (found as any).value : null;
    };

    const hasPrevValue =
      prevRaw !== undefined &&
      prevRaw !== null &&
      !(typeof prevRaw === 'string' && isBadToken(prevRaw));

    if (hasPrevValue) {
      if (isMulti) {
        let items: unknown[] = [];

        if (Array.isArray(prevRaw)) {
          items = prevRaw;
        } else if (typeof prevRaw === 'string') {
          const rawStr = prevRaw;

          if (!isBadToken(rawStr)) {
            try {
              const parsed: unknown = JSON.parse(rawStr);

              if (Array.isArray(parsed)) {
                items = parsed;
              } else {
                items = splitIds(rawStr);
              }
            } catch {
              items = splitIds(rawStr);
            }
          }
        }

        const pk = field.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';

        const looksLikeObjects =
          items.length > 0 &&
          items.every(x => {
            if (typeof x !== 'object' || x === null) {
              return false;
            }
            return (x as Record<string, unknown>)[pk] != null;
          });

        if (looksLikeObjects) {
          return items;
        }

        const ids = items
          .map(item => {
            if (typeof item === 'number') {
              return item;
            }
            if (typeof item === 'string') {
              return isBadToken(item) ? null : item;
            }
            if (typeof item === 'object' && item !== null) {
              const rec = item as Record<string, unknown>;
              const fromPk = rec[pk];
              const fromId = rec['id'];

              if (typeof fromPk === 'string' && !isBadToken(fromPk)) {
                return fromPk;
              }
              if (typeof fromPk === 'number') {
                return fromPk;
              }
              if (typeof fromId === 'string' && !isBadToken(fromId)) {
                return fromId;
              }
              if (typeof fromId === 'number') {
                return fromId;
              }
            }
            return null;
          })
          .filter((v): v is string | number => v !== null);

        const mapped = ids.map(id => mapIdToValue(id)).filter(v => v != null);

        if (mapped.length > 0) {
          return mapped;
        }

        // ✅ If finalized and nothing mapped -> clear
        if (this.dropdownsFinalized) {
          return [];
        }

        // Keep ids temporarily while options load
        return ids;
      }

      if (field.input?.dropdownConfiguration?.valuePrimaryKey) {
        const pk = field.input.dropdownConfiguration.valuePrimaryKey;

        if (typeof prevRaw === 'object' && prevRaw !== null) {
          const rec = prevRaw as Record<string, unknown>;
          if (rec[pk] != null) {
            return prevRaw;
          }
        }

        if (typeof prevRaw === 'string' && isBadToken(prevRaw)) {
          return '';
        }

        const mapped = mapIdToValue(prevRaw as string | number);

        if (mapped != null) {
          return mapped;
        }

        // ✅ If finalized and no match -> clear (avoid "null")
        if (this.dropdownsFinalized) {
          return '';
        }

        return prevRaw;
      }

      if (typeof prevRaw === 'string' && isBadToken(prevRaw)) {
        return isMulti ? [] : '';
      }

      return prevRaw;
    }

    if (field.input?.defaultValue == null) {
      return isMulti ? [] : '';
    }

    if (isMulti) {
      const def = field.input.defaultValue as unknown;

      if (Array.isArray(def)) {
        return def;
      }

      if (typeof def === 'string' && def.length > 0) {
        if (isBadToken(def)) {
          return [];
        }

        try {
          const parsed: unknown = JSON.parse(def);
          if (Array.isArray(parsed)) {
            return parsed;
          }
        } catch {
          // noop
        }

        const ids = splitIds(def);
        const mapped = ids.map(id => mapIdToValue(id)).filter(v => v != null);

        if (mapped.length > 0) {
          return mapped;
        }

        if (this.dropdownsFinalized) {
          return [];
        }

        return ids;
      }

      return [];
    }

    if (field.input?.dropdownConfiguration?.valuePrimaryKey) {
      const def = field.input.defaultValue;

      if (typeof def === 'string' && isBadToken(def)) {
        return '';
      }

      const mapped = mapIdToValue(def as string | number);

      if (mapped != null) {
        return mapped;
      }

      if (this.dropdownsFinalized) {
        return '';
      }

      return def;
    }

    return field.input.defaultValue;
  }

  private setValidators(field: FormField<T>): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (field.conditions.isRequired) {
      validators.push(Validators.required);
    }

    if (field.input.maxLength != null) {
      validators.push(Validators.maxLength(field.input.maxLength));
    }

    if (field.name?.includes('hora')) {
      validators.push(this.formCustomValidators.timeValidator);
    }

    if (field.name?.includes('email')) {
      validators.push(Validators.email);
    }

    if (field.name?.includes('nif') || field.name?.includes('cif')) {
      validators.push(this.formCustomValidators.spanishIdValidator);

      if (typeof field.checkForDuplicates === 'function') {
        validators.push(this.formCustomValidators.createDuplicateSpanishIdValidator(field.checkForDuplicates));
      }
    }

    return validators;
  }

  private readonly repeatableBindingControlNames = new Set<string>();

private indexRepeatableBindingControls(): void {
  this.repeatableBindingControlNames.clear();

  for (const section of this.form.sections) {
    if (!section.isVisible) continue;

    for (const field of section.fields) {
      if (!field.conditions.isVisible || field.conditions.isRetained) continue;
      if (field.input?.type !== (InputType as any).REPEATABLE_GROUP) continue;

      const bindings = field.input?.repeatableGroupConfiguration?.bindings ?? {};
      for (const ctrlName of Object.values(bindings)) {
        if (ctrlName) this.repeatableBindingControlNames.add(String(ctrlName));
      }
    }
  }
}

private patchRepeatableBindingsFromPreviousDataPristine(): void {
  const prev = this.previousData as Record<string, unknown> | null;
  if (!prev) return;

  for (const section of this.form.sections) {
    if (!section.isVisible) continue;

    for (const field of section.fields) {
      if (!field.conditions.isVisible || field.conditions.isRetained) continue;
      if (field.input?.type !== (InputType as any).REPEATABLE_GROUP) continue;

      const bindings = field.input?.repeatableGroupConfiguration?.bindings ?? {};

      for (const [logicalKey, targetControlName] of Object.entries(bindings)) {
        const ctrlName = String(targetControlName ?? '');
        if (!ctrlName) continue;

        const ctrl = this.model.get(ctrlName) as FormControl | null;
        if (!ctrl || !ctrl.pristine) continue;

        // Prioriza el dato real del backend (normalmente viene en ctrlName)
        const raw = prev[ctrlName] ?? prev[logicalKey];
        const next = raw == null ? '' : String(raw);

        if (String(ctrl.value ?? '') === next) continue;

        // emitEvent:true -> RepeatableGroup recibe valueChanges y rehidrata
        ctrl.patchValue(next, { emitEvent: true });
      }
    }
  }
}


}
